import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { AlertTriangle, Plus, MessageSquare, Clock, CheckCircle, AlertCircle, Filter, X, User, Shield, Send } from 'lucide-react';
import { canUserEdit } from '@/utils/permissions';

function Grievances() {
  const { API, user } = useContext(AppContext);
  const [grievances, setGrievances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedGrievance, setSelectedGrievance] = useState(null);
  const [updateNote, setUpdateNote] = useState('');
  const [formData, setFormData] = useState({
    category: 'Workplace',
    subject: '',
    description: '',
    isAnonymous: false
  });

  const categories = ['Workplace', 'Harassment', 'Discrimination', 'Safety', 'Management', 'Salary', 'Leave', 'Other'];
  const priorities = ['Low', 'Medium', 'High', 'Critical'];
  
  // Check if current user can manage grievances
  const isAdminOrHR = canUserEdit(user, '/hr/grievances');

  useEffect(() => {
    fetchGrievances();
  }, [statusFilter]);

  const fetchGrievances = async () => {
    try {
      let url = `${API}/hr/grievances?status=${statusFilter}`;
      // If not admin/HR, only show own grievances
      if (!isAdminOrHR && user?.id) {
        url += `&employeeId=${user.id}`;
      }
      const response = await axios.get(url);
      setGrievances(response.data);
    } catch (error) {
      console.error('Error fetching grievances:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitGrievance = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        employeeId: user?.id || 'EMP-001',
        employeeName: user?.name || 'Current User',
        ...formData
      };
      await axios.post(`${API}/hr/grievances`, payload);
      setShowSubmitModal(false);
      setFormData({ category: 'Workplace', subject: '', description: '', isAnonymous: false });
      fetchGrievances();
    } catch (error) {
      console.error('Error submitting grievance:', error);
      alert('Failed to submit grievance');
    }
  };

  const handleUpdateGrievance = async (updates) => {
    try {
      const payload = { ...updates };
      if (updateNote) payload.updateNote = updateNote;
      await axios.put(`${API}/hr/grievances/${selectedGrievance.id}`, payload);
      setUpdateNote('');
      fetchGrievances();
      // Refresh the selected grievance
      const response = await axios.get(`${API}/hr/grievances?status=all`);
      const updated = response.data.find(g => g.id === selectedGrievance.id);
      if (updated) setSelectedGrievance(updated);
    } catch (error) {
      console.error('Error updating grievance:', error);
      alert('Failed to update grievance');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Open': return <AlertCircle className="text-yellow-500" size={18} />;
      case 'In Progress': return <Clock className="text-blue-500" size={18} />;
      case 'Resolved': return <CheckCircle className="text-green-500" size={18} />;
      case 'Closed': return <CheckCircle className="text-gray-500" size={18} />;
      default: return <AlertCircle className="text-gray-500" size={18} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-yellow-100 text-yellow-700';
      case 'In Progress': return 'bg-blue-100 text-blue-700';
      case 'Resolved': return 'bg-green-100 text-green-700';
      case 'Closed': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'bg-red-100 text-red-700';
      case 'High': return 'bg-orange-100 text-orange-700';
      case 'Medium': return 'bg-yellow-100 text-yellow-700';
      case 'Low': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Harassment': return 'bg-red-100 text-red-700';
      case 'Discrimination': return 'bg-purple-100 text-purple-700';
      case 'Safety': return 'bg-orange-100 text-orange-700';
      case 'Management': return 'bg-blue-100 text-blue-700';
      case 'Salary': return 'bg-green-100 text-green-700';
      case 'Leave': return 'bg-cyan-100 text-cyan-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Summary stats
  const openCount = grievances.filter(g => g.status === 'Open').length;
  const inProgressCount = grievances.filter(g => g.status === 'In Progress').length;
  const resolvedCount = grievances.filter(g => g.status === 'Resolved').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a237e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="grievances-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="text-[#1a237e]" size={28} />
            Grievance Management
          </h1>
          <p className="text-gray-600 mt-1">
            {isAdminOrHR ? 'Review and resolve employee grievances' : 'Submit and track your grievances'}
          </p>
        </div>
        <button
          onClick={() => setShowSubmitModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593] transition"
          data-testid="submit-grievance-btn"
        >
          <Plus size={18} />
          Report Grievance
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total Grievances</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{grievances.length}</p>
            </div>
            <div className="h-12 w-12 bg-[#1a237e]/10 rounded-full flex items-center justify-center">
              <AlertTriangle className="text-[#1a237e]" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Open</p>
              <p className="text-2xl font-bold text-yellow-600 mt-1">{openCount}</p>
            </div>
            <div className="h-12 w-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <AlertCircle className="text-yellow-600" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">In Progress</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{inProgressCount}</p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Clock className="text-blue-600" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Resolved</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{resolvedCount}</p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="text-green-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <Filter size={18} className="text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] focus:border-[#1a237e] outline-none"
            data-testid="status-filter"
          >
            <option value="all">All Status</option>
            <option value="Open">Open</option>
            <option value="In Progress">In Progress</option>
            <option value="Resolved">Resolved</option>
            <option value="Closed">Closed</option>
          </select>
          <span className="text-sm text-gray-600">{grievances.length} grievances found</span>
        </div>
      </div>

      {/* Grievances List */}
      <div className="space-y-4">
        {grievances.map((grievance) => (
          <div
            key={grievance.id}
            className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition cursor-pointer"
            onClick={() => { setSelectedGrievance(grievance); setShowDetailModal(true); }}
            data-testid={`grievance-card-${grievance.id}`}
          >
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                  grievance.isAnonymous ? 'bg-gray-200' : 'bg-gradient-to-br from-[#1a237e] to-[#3949ab]'
                }`}>
                  {grievance.isAnonymous ? (
                    <Shield className="text-gray-500" size={20} />
                  ) : (
                    <span className="text-white font-bold">{grievance.employeeName?.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900">{grievance.subject}</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(grievance.status)}`}>
                      {grievance.status}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${getPriorityColor(grievance.priority)}`}>
                      {grievance.priority}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {grievance.isAnonymous ? 'Anonymous' : `${grievance.employeeName} | ${grievance.department}`}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getCategoryColor(grievance.category)}`}>
                      {grievance.category}
                    </span>
                    <span className="text-gray-500">
                      Submitted: {grievance.submittedDate}
                    </span>
                    {grievance.assignedTo && (
                      <span className="text-blue-600">
                        Assigned to: {grievance.assignedTo}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="text-sm text-gray-500">
                ID: {grievance.id}
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-600 line-clamp-2">{grievance.description}</p>
            </div>
          </div>
        ))}

        {grievances.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <AlertTriangle className="mx-auto text-gray-400 mb-3" size={48} />
            <p className="text-gray-500">No grievances found</p>
          </div>
        )}
      </div>

      {/* Submit Grievance Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Report a Grievance</h2>
              <button onClick={() => setShowSubmitModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmitGrievance} className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                <p className="font-medium">Confidential Submission</p>
                <p>Your grievance will be handled with utmost confidentiality by HR.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none"
                  data-testid="category-select"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none"
                  placeholder="Brief subject of your grievance"
                  data-testid="subject-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  required
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none resize-none"
                  placeholder="Please provide detailed description of your grievance..."
                  data-testid="description-input"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="anonymous"
                  checked={formData.isAnonymous}
                  onChange={(e) => setFormData({...formData, isAnonymous: e.target.checked})}
                  className="h-4 w-4 text-[#1a237e] border-gray-300 rounded focus:ring-[#1a237e]"
                  data-testid="anonymous-checkbox"
                />
                <label htmlFor="anonymous" className="text-sm text-gray-700">
                  Submit anonymously (your identity will be hidden)
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowSubmitModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593]"
                  data-testid="submit-grievance-form-btn"
                >
                  Submit Grievance
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Grievance Detail Modal */}
      {showDetailModal && selectedGrievance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-[#1a237e] to-[#283593] p-6 text-white flex justify-between items-start rounded-t-2xl">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 text-xs rounded-full bg-white/20`}>
                    {selectedGrievance.status}
                  </span>
                  <span className={`px-2 py-0.5 text-xs rounded-full bg-white/20`}>
                    {selectedGrievance.priority} Priority
                  </span>
                </div>
                <h2 className="text-lg font-bold">{selectedGrievance.subject}</h2>
                <p className="text-white/70 text-sm">{selectedGrievance.id}</p>
              </div>
              <button onClick={() => { setShowDetailModal(false); setSelectedGrievance(null); }} className="p-2 hover:bg-white/20 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Info Section */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Submitted By:</span>
                  <p className="font-medium flex items-center gap-2">
                    {selectedGrievance.isAnonymous ? (
                      <>
                        <Shield size={14} className="text-gray-400" />
                        Anonymous
                      </>
                    ) : (
                      <>
                        <User size={14} className="text-gray-400" />
                        {selectedGrievance.employeeName}
                      </>
                    )}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Department:</span>
                  <p className="font-medium">{selectedGrievance.department}</p>
                </div>
                <div>
                  <span className="text-gray-500">Category:</span>
                  <p className={`inline-block px-2 py-0.5 rounded-lg text-xs font-medium ${getCategoryColor(selectedGrievance.category)}`}>
                    {selectedGrievance.category}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Submitted Date:</span>
                  <p className="font-medium">{selectedGrievance.submittedDate}</p>
                </div>
                {selectedGrievance.assignedTo && (
                  <div>
                    <span className="text-gray-500">Assigned To:</span>
                    <p className="font-medium text-blue-600">{selectedGrievance.assignedTo}</p>
                  </div>
                )}
                {selectedGrievance.resolvedDate && (
                  <div>
                    <span className="text-gray-500">Resolved Date:</span>
                    <p className="font-medium text-green-600">{selectedGrievance.resolvedDate}</p>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Description</h4>
                <p className="text-gray-600 bg-gray-50 rounded-lg p-4">{selectedGrievance.description}</p>
              </div>

              {/* Resolution */}
              {selectedGrievance.resolution && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Resolution</h4>
                  <p className="text-green-700 bg-green-50 rounded-lg p-4">{selectedGrievance.resolution}</p>
                </div>
              )}

              {/* Updates Timeline */}
              {selectedGrievance.updates?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Updates</h4>
                  <div className="space-y-3">
                    {selectedGrievance.updates.map((update, index) => (
                      <div key={index} className="flex gap-3 text-sm">
                        <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <MessageSquare className="text-blue-600" size={14} />
                        </div>
                        <div className="flex-1 bg-gray-50 rounded-lg p-3">
                          <p className="text-gray-700">{update.note}</p>
                          <p className="text-xs text-gray-500 mt-1">{update.by} - {update.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin/HR Actions */}
              {isAdminOrHR && selectedGrievance.status !== 'Closed' && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Admin Actions</h4>
                  
                  {/* Add Update */}
                  <div className="flex gap-2 mb-4">
                    <input
                      type="text"
                      value={updateNote}
                      onChange={(e) => setUpdateNote(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none"
                      placeholder="Add an update note..."
                      data-testid="update-note-input"
                    />
                    <button
                      onClick={() => updateNote && handleUpdateGrievance({})}
                      className="px-4 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593] flex items-center gap-2"
                      data-testid="add-update-btn"
                    >
                      <Send size={16} />
                    </button>
                  </div>

                  {/* Status & Priority Controls */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Update Status</label>
                      <select
                        onChange={(e) => handleUpdateGrievance({ status: e.target.value })}
                        value={selectedGrievance.status}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1a237e] outline-none"
                        data-testid="status-select"
                      >
                        <option value="Open">Open</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Resolved">Resolved</option>
                        <option value="Closed">Closed</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Update Priority</label>
                      <select
                        onChange={(e) => handleUpdateGrievance({ priority: e.target.value })}
                        value={selectedGrievance.priority}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1a237e] outline-none"
                        data-testid="priority-select"
                      >
                        {priorities.map(p => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Assign & Resolve */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleUpdateGrievance({ assignedTo: user?.name || 'HR Admin' })}
                      className="flex-1 px-4 py-2 border border-[#1a237e] text-[#1a237e] rounded-lg hover:bg-[#1a237e]/10"
                      data-testid="assign-to-me-btn"
                    >
                      Assign to Me
                    </button>
                    {selectedGrievance.status !== 'Resolved' && (
                      <button
                        onClick={() => {
                          const resolution = prompt('Enter resolution details:');
                          if (resolution) handleUpdateGrievance({ status: 'Resolved', resolution });
                        }}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        data-testid="resolve-btn"
                      >
                        Mark as Resolved
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Grievances;
